import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoginService } from '../modulos/login/login.service';
import { WebSocketService } from './websocket';
import { api } from '../../environments/environment';
import { saveAs } from 'file-saver';
import { IToken } from 'modelos/src';

export interface IHttpRequestOptions {
  headers?: HttpHeaders;
  context?: HttpContext;
  observe?: 'body' | 'events' | 'response' | any;
  params?:
    | HttpParams
    | {
        [param: string]:
          string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: 'arraybuffer' | 'blob' | 'text' | 'json' | any;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private refreshing?: Promise<IToken>;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    webSocketService: WebSocketService,
  ) {
    // El socket fue rechazado con `tokenUsado`. No se rota por eso: el
    // rechazo puede ser de otra causa (gas-auth caído del lado del ws) y rotar
    // mata el token de las otras pestañas. Si otra pestaña ya lo renovó, se
    // reconecta con ese; si no, un GET liviano decide: sólo un 401 de verdad
    // pasa por el refresh coordinado (y ese refresh reconecta el socket).
    webSocketService.tokenRechazado$.subscribe((tokenUsado) => {
      const actual = LoginService.getToken();
      if (actual && actual !== tokenUsado) {
        webSocketService.asegurarConexion();
        return;
      }
      this.get('/usuarios/propio', undefined, true).subscribe({
        error: () => undefined,
      });
    });
  }

  /**
   * Descarga un archivo desde la api cliente
   * @param url
   * @param filename
   * @param params
   * @returns
   */
  public downloadFile(
    url: string,
    filename: string,
    params?: HttpParams,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.get(url, { params, responseType: 'blob' }).subscribe(
        (response: any) => {
          try {
            const file = new File([response], filename);
            saveAs(file);
            resolve();
          } catch (error) {
            console.error(error);
            resolve();
          }
        },
        (error: any) => {
          const reader: FileReader = new FileReader();
          reader.onloadend = () => {
            reject(JSON.parse(reader.result as string));
          };
          reader.readAsText(error.error);
        },
      );
    });
  }

  /**
   * Descarga un archivo pidiéndolo por POST.
   *
   * `downloadFile` manda el filtro por query string, y los exports cuyo filtro
   * se resuelve a miles de puntos (`idsAsignados.$in`) pasan el
   * `max-http-header-size` de Node: el server contesta 431 sin llegar al
   * handler. Por POST el filtro viaja en el body.
   */
  public downloadFilePost(
    url: string,
    filename: string,
    body: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.conReintentoDeSesion(() => this._postBlob(url, body)).subscribe(
        (response: any) => {
          try {
            const file = new File([response], filename);
            saveAs(file);
            resolve();
          } catch (error) {
            console.error(error);
            resolve();
          }
        },
        (error: any) => {
          // El cuerpo del error también viene como Blob (responseType: 'blob'),
          // así que hay que leerlo para sacar el mensaje del backend.
          const reader: FileReader = new FileReader();
          reader.onloadend = () => {
            reject(JSON.parse(reader.result as string));
          };
          reader.readAsText(error.error);
        },
      );
    });
  }

  /**
   * Obtiene un archivo desde cualquier URL
   * @param url
   * @param params
   * @returns
   */
  public getFile(url: string, params?: HttpParams): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http.get(url, { params, responseType: 'blob' }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (error: any) => {
          const reader: FileReader = new FileReader();
          reader.onloadend = () => {
            reject(JSON.parse(reader.result as string));
          };
          reader.readAsText(error.error);
        },
      );
    });
  }

  public get(
    ruta: string,
    options?: IHttpRequestOptions,
    noCache = false,
  ): Observable<any> {
    return this.conReintentoDeSesion(() => this._get(ruta, options, noCache));
  }

  public post(ruta: string, body: any): Observable<any> {
    return this.conReintentoDeSesion(() => this._post(ruta, body));
  }

  /**
   * POST que responde en eventos (text/event-stream): llama a `alEvento` con cada `data:`.
   * HttpClient no entrega el cuerpo de a poco, por eso va con fetch. Si el backend rechaza
   * por sesión antes de empezar, refresca el token (el mismo refresco coordinado) y reintenta
   * una vez.
   */
  public async postStream(ruta: string, body: unknown, alEvento: (dato: any) => void): Promise<void> {
    const pedir = () =>
      fetch(`${api}${ruta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${LoginService.getToken()}` },
        body: JSON.stringify(body),
      });
    const tokenUsado = LoginService.getToken();
    let res = await pedir();
    if (res.status === 401) {
      await this.refrescarSesion(tokenUsado);
      res = await pedir();
    }
    if (!res.ok || !res.body) throw { status: res.status };
    const lector = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let resto = '';
    for (;;) {
      const { value, done } = await lector.read();
      if (done) break;
      resto += value;
      const eventos = resto.split('\n\n');
      resto = eventos.pop() ?? '';
      for (const ev of eventos) {
        if (ev.startsWith('data: ')) alEvento(JSON.parse(ev.slice(6)));
      }
    }
  }

  public put(ruta: string, body: any): Observable<any> {
    return this.conReintentoDeSesion(() => this._put(ruta, body));
  }

  public patch(ruta: string, body: any): Observable<any> {
    return this.conReintentoDeSesion(() => this._patch(ruta, body));
  }

  public delete(ruta: string): Observable<any> {
    return this.conReintentoDeSesion(() => this._delete(ruta));
  }

  /**
   * Ejecuta la request y, si el backend rechaza por sesión, refresca el token y
   * reintenta UNA sola vez.
   *
   * Antes cada verbo tenía su copia de esta lógica y todas comparaban el mensaje
   * de error con un string exacto: cualquier cambio de redacción en el backend
   * dejaba de disparar el refresh y el usuario terminaba viendo errores hasta
   * recargar. Ahora se mira el status 401, que es el contrato real.
   */
  private conReintentoDeSesion<T>(operacion: () => Promise<T>): Observable<T> {
    return new Observable((observer) => {
      // El token con el que sale el pedido: si falla y mientras tanto otra
      // pestaña ya lo renovó, no hay que rotar de nuevo (ver refreshToken).
      const tokenUsado = LoginService.getToken();
      operacion()
        .then((resp) => {
          observer.next(resp);
          observer.complete();
        })
        .catch(async (err) => {
          if (!HttpService.esSesionVencida(err)) {
            observer.error(err);
            return;
          }
          if (err?.error?.codigo === 'sesion-revocada') {
            this.loginService.cerrarSesion(err.error.motivo, true);
            observer.error(err);
            return;
          }
          try {
            await this.refrescarSesion(tokenUsado);
          } catch (errorRefresh) {
            observer.error(errorRefresh);
            return;
          }
          // Un solo reintento: si vuelve a fallar, se propaga. Sin esto, un 401
          // persistente encadenaría refreshes en loop.
          operacion()
            .then((resp) => {
              observer.next(resp);
              observer.complete();
            })
            .catch((errorReintento) => observer.error(errorReintento));
        });
    });
  }

  // Un solo refresh en vuelo por más requests que fallen a la vez. El finally es
  // lo que faltaba antes: la promesa quedaba cacheada incluso cuando había
  // fallado, así que todas las requests siguientes reusaban un rechazo viejo y
  // la sesión no se recuperaba nunca.
  private refrescarSesion(tokenUsado?: string): Promise<IToken> {
    if (!this.refreshing) {
      this.refreshing = this.loginService
        .refreshToken(tokenUsado)
        .finally(() => {
          this.refreshing = undefined;
        });
    }
    return this.refreshing;
  }

  private static esSesionVencida(err: unknown): boolean {
    const error = err as { status?: number; error?: { message?: string } };
    if (error?.status === 401) return true;
    // Se mantiene el match por mensaje para los casos en que el error llega sin
    // status (ej. envuelto por un proxy).
    return error?.error?.message === 'Invalid token: access token has expired';
  }

  private _get<T>(
    ruta: string,
    options?: IHttpRequestOptions,
    noCache = false,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      // Headers armados de nuevo en cada intento, sin tocar el `options` del
      // que llama: antes el reintento después de un refresh salía con
      // "Authorization: Bearer <viejo>,Bearer <nuevo>" y la API lo rechazaba.
      let headers = (options?.headers ?? new HttpHeaders()).set(
        'Authorization',
        `Bearer ${LoginService.getToken()}`,
      );
      if (noCache) {
        headers = headers.set('no-cache', 'true');
      }

      // Request
      this.http.get<T>(`${api}${ruta}`, { ...options, headers }).subscribe(
        (resp) => {
          resolve(resp);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  private _post(ruta: string, body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${LoginService.getToken()}`,
      });
      const req = this.http.post<any>(`${api}${ruta}`, body, { headers });
      req.subscribe(
        (resp) => {
          resolve(resp);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  private _postBlob(ruta: string, body: any): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${LoginService.getToken()}`,
      });
      this.http
        .post(`${api}${ruta}`, body, { headers, responseType: 'blob' })
        .subscribe(
          (resp) => {
            resolve(resp);
          },
          (err) => {
            reject(err);
          },
        );
    });
  }

  private _put(ruta: string, body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${LoginService.getToken()}`,
      });
      const req = this.http.put<any>(`${api}${ruta}`, body, { headers });
      req.subscribe(
        (resp) => {
          resolve(resp);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  private _patch(ruta: string, body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${LoginService.getToken()}`,
      });
      const req = this.http.patch<any>(`${api}${ruta}`, body, { headers });
      req.subscribe(
        (resp) => {
          resolve(resp);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  private _delete(ruta: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${LoginService.getToken()}`,
      });
      const req = this.http.delete<any>(`${api}${ruta}`, { headers });
      req.subscribe(
        (resp) => {
          resolve(resp);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }
}
